import { useEffect, useState } from "react";
import { Wallet, Sparkles, Check, History } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useWallet } from "../context/WalletContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const LEDGER_LABELS = {
  credit_purchase: "Achat de crédits",
  consultation_debit: "Consultation",
  bonus_bienvenue: "Bonus de bienvenue",
  refund: "Remboursement",
  adjustment: "Ajustement",
};

export default function Credits() {
  const { balance, refresh } = useWallet();
  const [packs, setPacks] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [busy, setBusy] = useState(null);
  const [paymentsLive, setPaymentsLive] = useState(false);

  useEffect(() => {
    api.creditPacks().then(({ data }) => setPacks(data)).catch(() => {});
    api.ledger().then(({ data }) => setLedger(data)).catch(() => {});
    api.config().then(({ data }) => setPaymentsLive(!!data.payments_live)).catch(() => {});
  }, []);

  const buy = async (pack) => {
    if (!paymentsLive) { toast.info("Rechargement bientôt disponible."); return; }
    setBusy(pack.lookup_key);
    try {
      const { data } = await api.checkout(pack.lookup_key);
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(null);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
      <div className="glass flex flex-col items-start justify-between gap-4 rounded-3xl border border-celeste-or/20 p-6 sm:flex-row sm:items-center">
        <div>
          <p className="text-sm text-celeste-lilas/70">Votre solde</p>
          <p className="mt-1 flex items-center gap-2 font-serif text-4xl text-celeste-or"><Wallet className="h-8 w-8" /> {balance ?? "…"} <span className="text-lg">crédits</span></p>
        </div>
        <p className="max-w-xs text-xs text-celeste-lilas/60">Crédits prépayés · aucun abonnement · aucun renouvellement automatique.{paymentsLive ? " Paiement sécurisé." : " Le rechargement ouvrira prochainement."}</p>
      </div>

      {!paymentsLive && (
        <div className="mt-6 rounded-2xl border border-celeste-or/25 bg-celeste-prune/30 p-4 text-sm text-celeste-lilas" data-testid="recharge-soon-banner">
          Le rechargement de crédits ouvrira prochainement. La plateforme Essences Céleste ouvre progressivement.
        </div>
      )}

      <h2 className="mt-10 font-serif text-2xl text-celeste-ivoire">Recharger mon portefeuille</h2>
      <div className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {packs.map((p) => (
          <div key={p.lookup_key} className={`glass card-hover relative rounded-3xl border p-6 ${p.lookup_key === "pack_serenite" ? "border-celeste-or/60" : "border-celeste-or/15"}`} data-testid={`pack-${p.lookup_key}`}>
            {p.lookup_key === "pack_serenite" && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full gold-btn px-3 py-0.5 text-[11px] font-semibold">Populaire</span>
            )}
            <Sparkles className="h-6 w-6 text-celeste-or" />
            <h3 className="mt-3 font-serif text-lg text-celeste-ivoire">{p.name}</h3>
            <p className="mt-1 text-sm text-celeste-lilas/70">{p.description}</p>
            <p className="mt-4 font-serif text-3xl text-celeste-or">{p.credits} <span className="text-base text-celeste-lilas">crédits</span></p>
            <p className="text-sm text-celeste-lilas/60">{p.price_eur.toFixed(2)} €</p>
            <Button data-testid={`buy-${p.lookup_key}`} onClick={() => buy(p)} disabled={!paymentsLive || busy === p.lookup_key}
              className="mt-4 w-full gold-btn font-semibold disabled:opacity-50">
              {!paymentsLive ? "Bientôt disponible" : busy === p.lookup_key ? "Redirection…" : "Acheter"}
            </Button>
          </div>
        ))}
      </div>

      <h2 className="mt-12 flex items-center gap-2 font-serif text-2xl text-celeste-ivoire"><History className="h-5 w-5 text-celeste-or" /> Historique du portefeuille</h2>
      <div className="glass mt-4 rounded-3xl border border-celeste-or/15 p-2 sm:p-4" data-testid="ledger-list">
        {ledger.length === 0 ? (
          <p className="p-6 text-center text-sm text-celeste-lilas/60">Aucun mouvement pour le moment.</p>
        ) : (
          <div className="divide-y divide-celeste-violet/15">
            {ledger.map((l) => (
              <div key={l.id} className="flex items-center justify-between px-3 py-3 text-sm">
                <div>
                  <p className="text-celeste-ivoire">{LEDGER_LABELS[l.type] || l.type}</p>
                  <p className="text-xs text-celeste-lilas/50">{new Date(l.created_at).toLocaleString("fr-FR")}</p>
                </div>
                <div className="text-right">
                  <p className={l.amount >= 0 ? "text-emerald-300" : "text-red-300"}>{l.amount >= 0 ? "+" : ""}{Math.round(l.amount * 100) / 100} cr</p>
                  <p className="text-xs text-celeste-lilas/50">Solde : {Math.round(l.balance_after * 100) / 100}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
